import ContentLayout from "../UI/Layout/ContentLayout.tsx";
import { Tabs } from "../UI/Tabs/Tabs";
import MailTemplatesList from "../Account/MailTemplatesList";
import {
  IconFileExport,
  IconFileText,
  IconReportMoney,
  IconShare,
  IconTableExport,
} from "@tabler/icons-react";
import HeaderProfileBailleur from "./HeaderProfileBailleur";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import BailleurProfileList from "./BailleurProfileList";
import CardAction from "../UI/CardAction";
import ProfileNomShareForm from "./ProfileNomShareForm";
// @ts-expect-error pure js
import ProfileRecapFiscal from "./ProfileRecapFiscal";
// @ts-expect-error pure js
import ExportComptableForm from "./ExportComptableForm";
import ExportFecForm from "./ExportFecForm";
import CrgGenerateForm from "./CrgGenerateForm";
// @ts-expect-error pure js
import ProfileBailleurForm from "./ProfileBailleurForm";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
import { useState } from "react";
import styles from "./ProfileBailleurShow.module.css";
import ParametreProfileBailleur from "./ParametreProfileBailleur";
import AutomatisationProfileBailleur from "./AutomatisationProfileBailleur";
import DocumentShowList from "../File/DocumentShowList.jsx";
import PretList from "../Pret/PretList";
// @ts-expect-error pure js
import useAuth from "../../hooks/use-auth.js";
import ComptaShow from "../Property/ComptaShow";
import { BalanceDomain } from "../../utils/constants/balances";
// @ts-expect-error pure js
import { StatusFiscal } from "../../models/profile.js";
import type { Droits, ProfileBailleur } from "./types";
import PermissionScope from "../../store/PermissionScope";
import GuidedTour from "../GuidedTour/GuidedTour";
import { BAILLEUR_GUIDE_ID, BAILLEUR_GUIDE_STEPS } from "../GuidedTour/guides";
import useOnboarding from "../../hooks/use-onboarding";
import { useNavigate } from "react-router-dom";

interface ProfileBailleurShowProps {
  profileBailleurId: number;
}

export default function ProfileBailleurShow({
  profileBailleurId,
}: ProfileBailleurShowProps) {
  const {
    profileBailleursById,
    shareProfileBailleur,
    updateSharedProfileDroits,
    deleteSharedProfileBailleur,
  } = useProfileBailleurs({
    withById: true,
  });
  const profileBailleur: ProfileBailleur | undefined =
    profileBailleursById[profileBailleurId];
  const { auth, isPro } = useAuth();
  const { gating } = useOnboarding();
  const nav = useNavigate();

  const [RecapFiscalModal, , openRecapFiscal] = useModal();
  const [ExportComptableModal, , openExportComptableModal] = useModal();
  const [exportFecOpen, setExportFecOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [CrgModal, closeCrgModal, openCrgModal] = useModal();
  const [
    ProfileBailleurFormModal,
    closeProfileBailleurFormModal,
    openProfileBailleurFormModal,
  ] = useModal();

  async function shareProfileHandler(
    values: { mail: string; droits: Droits },
    { resetForm }: { resetForm: () => void },
  ) {
    await shareProfileBailleur(profileBailleur!.id, values.mail, values.droits);
    resetForm();
  }
  if (!profileBailleur) return null;

  return (
    <PermissionScope profileId={profileBailleur?.id}>
      <ContentLayout
        header={
          profileBailleur && (
            <HeaderProfileBailleur profileBailleur={profileBailleur} />
          )
        }
        body={
          <>
            <ProfileNomShareForm
              open={isShareModalOpen}
              onOpenChange={setIsShareModalOpen}
              sharedComptes={(profileBailleur.comptes ?? []).filter(
                (compte) => compte.mail !== auth.mail,
              )}
              onAddShare={shareProfileHandler}
              onUpdateDroits={(mail, droits) =>
                updateSharedProfileDroits(profileBailleur.id, mail, droits)
              }
              onDeleteShare={(mail) =>
                deleteSharedProfileBailleur(profileBailleur.id, mail)
              }
            />
            <RecapFiscalModal>
              <ProfileRecapFiscal profileBailleurId={profileBailleur.id} />
            </RecapFiscalModal>
            <ExportComptableModal>
              <ExportComptableForm profileBailleurId={profileBailleur.id} />
            </ExportComptableModal>
            <ExportFecForm
              profileBailleurId={profileBailleur.id}
              open={exportFecOpen}
              onOpenChange={setExportFecOpen}
            />
            <CrgModal>
              <CrgGenerateForm
                profileBailleurId={profileBailleur.id}
                defaultMail={profileBailleur.mail || auth.mail}
                onClose={closeCrgModal}
              />
            </CrgModal>
            <ProfileBailleurFormModal>
              <ProfileBailleurForm
                profileBailleur={profileBailleur}
                onSubmit={closeProfileBailleurFormModal}
              />
            </ProfileBailleurFormModal>
            <Tabs id="bailleur-show" defaultValue="vue-ensemble">
              <div className="container bg-blue paddingT10 border-blue">
                <div className="content">
                  <Tabs.List>
                    <Tabs.Tab
                      value="vue-ensemble"
                      dataTour="bailleur-tab-general"
                    >
                      Général
                    </Tabs.Tab>
                    {isPro && (
                      <Tabs.Tab value="comptabilite">Comptabilité</Tabs.Tab>
                    )}
                    <Tabs.Tab
                      value="documents"
                      dataTour="bailleur-tab-documents"
                    >
                      Documents
                    </Tabs.Tab>
                    <Tabs.Tab value="prets" dataTour="bailleur-tab-prets">
                      Prêts
                    </Tabs.Tab>
                    <Tabs.Tab
                      value="automatisation"
                      dataTour="bailleur-tab-automatisation"
                    >
                      Automatisation
                    </Tabs.Tab>
                    <Tabs.Tab
                      value="parametres"
                      dataTour="bailleur-tab-parametres"
                    >
                      Paramètres
                    </Tabs.Tab>
                    <Tabs.Tab value="modeles-emails">
                      Modèles d'e-mails
                    </Tabs.Tab>
                  </Tabs.List>
                </div>
              </div>
              <div className="blueSpace"></div>
              <div className="content">
                <Tabs.Panel value="vue-ensemble">
                  <div className={styles.grid}>
                    <CardAction
                      icon={IconFileExport}
                      description={
                        "Exportez les opérations comptables liées aux biens de ce profil bailleur."
                      }
                      title={"Export comptable"}
                      onClick={openExportComptableModal}
                    />
                    <CardAction
                      icon={IconTableExport}
                      description={
                        "Exportez les écritures comptables au format FEC (DGFIP)."
                      }
                      title={"Export FEC"}
                      onClick={() => setExportFecOpen(true)}
                    />
                    {![
                      StatusFiscal.LMNPMICRO,
                      StatusFiscal.LMNPREEL,
                      StatusFiscal.IS,
                    ].includes(profileBailleur.regime_fiscal) && (
                      <CardAction
                        icon={IconReportMoney}
                        description={
                          "Générer le document d'aide à la déclaration des revenus fonciers."
                        }
                        title={"Déclaration revenus fonciers"}
                        onClick={openRecapFiscal}
                      />
                    )}
                    <CardAction
                      icon={IconFileText}
                      description={
                        "Générer le compte rendu de gestion pour un mois donné."
                      }
                      title={"Compte rendu de gestion"}
                      onClick={openCrgModal}
                      requiresWrite
                    />
                    {auth.userId === profileBailleur.id_compte && (
                      <CardAction
                        icon={IconShare}
                        description={
                          "Partagez la gestion de ce profil bailleur avec une ou plusieurs autres personnes."
                        }
                        title={"Partagez la gestion"}
                        onClick={() => setIsShareModalOpen(true)}
                      />
                    )}
                  </div>
                  <div data-tour="bailleur-infos-persos">
                    <BailleurProfileList
                      profiles={profileBailleur.profiles}
                      onEditProfile={openProfileBailleurFormModal}
                    />
                  </div>
                </Tabs.Panel>
                {isPro && (
                  <Tabs.Panel value="comptabilite">
                    <ComptaShow
                      parentId={profileBailleur.id}
                      domaineBalance={BalanceDomain.PROFILE_BAILLEUR}
                      soldeTitle="Solde comptable du profil"
                    />
                  </Tabs.Panel>
                )}
                <Tabs.Panel value="documents">
                  <DocumentShowList
                    profileBailleurId={profileBailleur.id}
                    allowDelete
                    allowEdit
                    isBailleur
                  />
                </Tabs.Panel>
                <Tabs.Panel value="prets">
                  <PretList profileBailleurId={profileBailleur.id} />
                </Tabs.Panel>
                <Tabs.Panel value="automatisation">
                  <AutomatisationProfileBailleur
                    profileBailleur={profileBailleur}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="parametres">
                  <ParametreProfileBailleur profileBailleur={profileBailleur} />
                </Tabs.Panel>
                <Tabs.Panel value="modeles-emails">
                  <MailTemplatesList
                    profileBailleurId={profileBailleur.id}
                    isProfile
                  />
                </Tabs.Panel>
              </div>
            </Tabs>
            <GuidedTour
              guideId={BAILLEUR_GUIDE_ID}
              steps={BAILLEUR_GUIDE_STEPS}
              run={gating}
              onFinish={(finished) => {
                if (finished) {
                  nav("/bailleur");
                }
              }}
            />
          </>
        }
      />
    </PermissionScope>
  );
}
